import JSZip from "jszip";
import { tokenManager } from "../../api/tokenManager";

export interface FileToDownload {
  entityName: string;
  fieldName: string;
  instanceId: string;
  fileName: string;
  category: string;
}

export interface DownloadResult {
  total: number;
  successful: number;
  failed: Array<{ fileName: string; category: string; error: string }>;
}

export class UserCancelledError extends Error {
  constructor() {
    super("Descarga cancelada por el usuario");
    this.name = "UserCancelledError";
  }
}

type ProgressCallback = (current: number, total: number, fileName: string, category: string) => void;

const supportsFileSystemAccess = (): boolean => {
  return "showDirectoryPicker" in window;
};

const sanitizeFolderName = (name: string): string => {
  return name.replace(/[<>:"/\\|?*]/g, "_").trim();
};

function isUserAbortError(error: unknown): boolean {
  if (error instanceof DOMException) {
    return error.name === "AbortError" || error.name === "NotAllowedError";
  }
  return false;
}

async function checkFolderExists(
  parentHandle: FileSystemDirectoryHandle,
  folderName: string
): Promise<boolean> {
  try {
    await parentHandle.getDirectoryHandle(folderName, { create: false });
    return true;
  } catch {
    return false;
  }
}

async function deleteFolder(
  parentHandle: FileSystemDirectoryHandle,
  folderName: string
): Promise<void> {
  await parentHandle.removeEntry(folderName, { recursive: true });
}

async function fetchFileBlob(file: FileToDownload, signal?: AbortSignal): Promise<Blob> {
  const apiUrl = process.env.REACT_APP_API_URL || "http://localhost:4000/graphql";
  const serverUrl = apiUrl.replace(/\/graphql$/, "");

  const downloadUrl = `/${file.entityName}.${file.fieldName}/${file.instanceId}/${encodeURIComponent(file.fileName)}`;
  const token = await tokenManager.getValidAccessToken();

  const response = await fetch(`${serverUrl}/download${downloadUrl}`, {
    method: "GET",
    headers: token ? { Authorization: `Bearer ${token}` } : {},
    signal,
  });

  if (!response.ok) {
    throw new Error(`HTTP ${response.status}`);
  }

  return response.blob();
}

async function downloadWithFileSystemAccess(
  downloadName: string,
  files: FileToDownload[],
  onProgress?: ProgressCallback,
  signal?: AbortSignal,
  onAskOverwrite?: (folderName: string) => Promise<boolean>
): Promise<DownloadResult> {
  const result: DownloadResult = {
    total: files.length,
    successful: 0,
    failed: [],
  };

  const dirHandle = await (window as any).showDirectoryPicker({
    mode: "readwrite",
  });

  const sanitizedName = sanitizeFolderName(downloadName);

  // Check if folder already exists
  const folderExists = await checkFolderExists(dirHandle, sanitizedName);

  if (folderExists) {
    if (onAskOverwrite) {
      const shouldOverwrite = await onAskOverwrite(sanitizedName);
      if (!shouldOverwrite) {
        throw new UserCancelledError();
      }
      // Delete existing folder before creating new one
      await deleteFolder(dirHandle, sanitizedName);
    }
  }

  const mainDirHandle = await dirHandle.getDirectoryHandle(sanitizedName, {
    create: true,
  });

  // Track if we need cleanup on cancel
  let downloadStarted = false;
  let downloadCompleted = false;

  try {
    const categoryHandles: Record<string, any> = {};

    for (let i = 0; i < files.length; i++) {
      if (signal?.aborted) {
        throw new UserCancelledError();
      }

      downloadStarted = true;
      const file = files[i];

      if (onProgress) {
        onProgress(i + 1, files.length, file.fileName, file.category);
      }

      try {
        if (!categoryHandles[file.category]) {
          categoryHandles[file.category] = await mainDirHandle.getDirectoryHandle(
            file.category,
            { create: true }
          );
        }

        const blob = await fetchFileBlob(file, signal);
        const categoryHandle = categoryHandles[file.category];
        const fileHandle = await categoryHandle.getFileHandle(file.fileName, {
          create: true,
        });
        const writable = await fileHandle.createWritable();
        await writable.write(blob);
        await writable.close();

        result.successful++;
      } catch (error) {
        if (signal?.aborted || (error instanceof DOMException && error.name === "AbortError")) {
          throw new UserCancelledError();
        }
        result.failed.push({
          fileName: file.fileName,
          category: file.category,
          error: error instanceof Error ? error.message : String(error),
        });
      }
    }

    downloadCompleted = true;
    return result;
  } finally {
    // Cleanup: delete folder if download was cancelled after starting
    if (downloadStarted && !downloadCompleted && signal?.aborted) {
      try {
        await deleteFolder(dirHandle, sanitizedName);
      } catch (e) {
        console.warn("Error during cleanup:", e);
      }
    }
  }
}

async function downloadAsZip(
  downloadName: string,
  files: FileToDownload[],
  onProgress?: ProgressCallback,
  signal?: AbortSignal
): Promise<DownloadResult> {
  const result: DownloadResult = {
    total: files.length,
    successful: 0,
    failed: [],
  };

  const zip = new JSZip();
  const categoryFolders: Record<string, any> = {};

  for (let i = 0; i < files.length; i++) {
    if (signal?.aborted) {
      throw new UserCancelledError();
    }

    const file = files[i];

    if (onProgress) {
      onProgress(i + 1, files.length, file.fileName, file.category);
    }

    try {
      if (!categoryFolders[file.category]) {
        const folder = zip.folder(file.category);
        if (!folder) {
          throw new Error(`No se pudo crear la carpeta ${file.category}`);
        }
        categoryFolders[file.category] = folder;
      }

      const blob = await fetchFileBlob(file, signal);
      const folder = categoryFolders[file.category];
      folder.file(file.fileName, blob);
      result.successful++;
    } catch (error) {
      if (signal?.aborted || (error instanceof DOMException && error.name === "AbortError")) {
        throw new UserCancelledError();
      }
      result.failed.push({
        fileName: file.fileName,
        category: file.category,
        error: error instanceof Error ? error.message : String(error),
      });
    }
  }

  if (signal?.aborted) {
    throw new UserCancelledError();
  }

  const zipBlob = await zip.generateAsync({ type: "blob" });
  const sanitizedName = sanitizeFolderName(downloadName);
  const blobUrl = window.URL.createObjectURL(zipBlob);

  const link = document.createElement("a");
  link.href = blobUrl;
  link.download = `${sanitizedName}.zip`;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);

  setTimeout(() => window.URL.revokeObjectURL(blobUrl), 100);

  return result;
}

export async function downloadAllFilesInFolder(
  downloadName: string,
  files: FileToDownload[],
  onProgress?: ProgressCallback,
  signal?: AbortSignal,
  onAskOverwrite?: (folderName: string) => Promise<boolean>
): Promise<DownloadResult> {
  if (files.length === 0) {
    return {
      total: 0,
      successful: 0,
      failed: [],
    };
  }

  if (supportsFileSystemAccess()) {
    try {
      return await downloadWithFileSystemAccess(downloadName, files, onProgress, signal, onAskOverwrite);
    } catch (error) {
      if (isUserAbortError(error) || error instanceof UserCancelledError) {
        throw new UserCancelledError();
      }
      console.warn("Filesystem Access API falló, usando fallback ZIP:", error);
      return await downloadAsZip(downloadName, files, onProgress, signal);
    }
  } else {
    return await downloadAsZip(downloadName, files, onProgress, signal);
  }
}
